/**
 * IBAN validation, mirroring IbanUtils on the backend.
 *
 * Kept in step with
 *   src/ZeitonBackend.API/Features/Finance/Invoices/TransferLists/Utils/IbanUtils.cs
 * so the export grid rejects the same values the server would, instead of letting the user submit
 * and get a 400 back.
 */

const IBAN_MIN_LENGTH = 15;
const IBAN_MAX_LENGTH = 34;
const IBAN_STRUCTURE = /^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/;

/**
 * Strips separators and upper-cases. IBANs are stored as `"AT61" + " " + base` and users paste them
 * in four-character groups, so nothing can be checked before this runs.
 */
export function normalizeIban(value: string | null | undefined): string {
    if (!value) return '';

    return value.replace(/[^0-9a-zA-Z]/g, '').toUpperCase();
}

/** Shape only: two letters, two check digits, then up to 30 alphanumerics, 15-34 in total. */
export function isStructurallyValidIban(normalized: string): boolean {
    return normalized.length >= IBAN_MIN_LENGTH
        && normalized.length <= IBAN_MAX_LENGTH
        && IBAN_STRUCTURE.test(normalized);
}

/**
 * ISO 7064 MOD-97-10.
 *
 * The first four characters move to the end and letters expand to 10..35. Folded with a running
 * remainder because the expanded number has up to 60 digits, well past Number.MAX_SAFE_INTEGER.
 */
function mod97(iban: string): number {
    let remainder = 0;

    for (let i = 0; i < iban.length; i++) {
        const code = iban.charCodeAt((i + 4) % iban.length);

        remainder = code >= 48 && code <= 57
            ? (remainder * 10 + (code - 48)) % 97
            : (remainder * 100 + (code - 55)) % 97;  // 'A' (65) -> 10, so subtract 55
    }

    return remainder;
}

/**
 * Structure plus the mod-97 checksum.
 *
 * Deliberately no per-country length table. It would only add the case of a truncated or padded
 * IBAN that still happens to satisfy mod-97 - roughly one in 97 - while a single wrong entry in it,
 * or a country that adopts IBAN later, would reject a perfectly good IBAN and block a real payment.
 */
export function isValidIban(value: string | null | undefined): boolean {
    const iban = normalizeIban(value);

    return isStructurallyValidIban(iban) && mod97(iban) === 1;
}
